import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Contact = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Serrurier Villeurbanne – Tolstoï Serrurier | 06 95 22 00 18"
        description="Contactez votre serrurier à Villeurbanne et Lyon. Intervention 24h/24 et 7j/7. Devis gratuit par téléphone au 06 95 22 00 18 ou via notre formulaire de contact."
        keywords="contact serrurier Villeurbanne, serrurier Lyon contact, devis serrurerie, contacter serrurier urgence"
        canonical="https://www.tolstoi-serrurier.fr/contact"
        ogUrl="https://www.tolstoi-serrurier.fr/contact"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
      />
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={[
            { label: "Accueil", href: "/" },
            { label: "Contact", href: "/contact" }
          ]} />
          
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Contactez-nous</h1>
              <p className="text-xl text-muted-foreground">
                Une urgence ? Un devis ? Nous sommes à votre écoute 24h/24 et 7j/7
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Informations de contact */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-primary" />
                      Téléphone
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a 
                      href="tel:0695220018" 
                      className="text-2xl font-bold text-primary hover:underline"
                    >
                      06 95 22 00 18
                    </a>
                    <p className="text-muted-foreground mt-2">
                      Disponible 24h/24 et 7j/7 pour vos urgences
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Email
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a 
                      href="mailto:serruriertolstoi@gmail.com" 
                      className="text-lg text-primary hover:underline break-all"
                    >
                      serruriertolstoi@gmail.com
                    </a>
                    <p className="text-muted-foreground mt-2">
                      Réponse sous 24h maximum
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      Zone d'intervention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-semibold">Villeurbanne et Grand Lyon</p>
                    <p className="text-muted-foreground mt-2">
                      Nous intervenons dans tous les quartiers de Villeurbanne, 
                      les 9 arrondissements de Lyon et toute la métropole
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-primary" />
                      Horaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-semibold text-primary">Ouvert 24h/24 - 7j/7</p>
                    <p className="text-muted-foreground mt-2">
                      Intervention en urgence jour et nuit, week-ends et jours fériés
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Formulaire de contact */}
              <Card>
                <CardHeader>
                  <CardTitle>Envoyez-nous un message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form 
                    action="https://formsubmit.co/serruriertolstoi@gmail.com" 
                    method="POST"
                    className="space-y-4"
                  >
                    {/* Configuration FormSubmit */}
                    <input type="hidden" name="_subject" value="Nouveau message depuis le site Tolstoï Serrurier" />
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_template" value="table" />
                    <input type="hidden" name="_next" value="https://www.tolstoi-serrurier.fr/contact?success=true" />

                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Votre nom et prénom"
                        required
                        maxLength={100}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="votre@email.com"
                        required
                        maxLength={255}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone *</Label>
                      <Input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="06 12 34 56 78"
                        required
                        maxLength={20}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ville">Ville</Label>
                      <Input
                        type="text"
                        id="ville"
                        name="ville"
                        placeholder="Ex: Villeurbanne, Lyon 3, Bron..."
                        maxLength={100}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service">Type de service</Label>
                      <select 
                        id="service"
                        name="service"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="">Sélectionnez un service</option>
                        <option value="Dépannage d'urgence">Dépannage d'urgence</option>
                        <option value="Ouverture de porte">Ouverture de porte</option>
                        <option value="Changement de serrure">Changement de serrure</option>
                        <option value="Réparation de serrure">Réparation de serrure</option>
                        <option value="Porte blindée">Porte blindée</option>
                        <option value="Volet roulant">Volet roulant</option>
                        <option value="Devis">Demande de devis</option>
                        <option value="Autre">Autre demande</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Décrivez votre besoin..."
                        required
                        maxLength={2000}
                        rows={6}
                        className="w-full resize-none"
                      />
                    </div>

                    <Button type="submit" size="lg" className="w-full">
                      Envoyer le message
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      * Champs obligatoires
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Section urgence */}
            <Card className="bg-primary text-primary-foreground">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Besoin d'une intervention urgente ?</h2>
                <p className="text-lg mb-6 text-primary-foreground/90">
                  Pour toute urgence, appelez-nous directement. Un serrurier vous répondra immédiatement.
                </p>
                <Button 
                  size="lg" 
                  variant="secondary" 
                  className="text-lg px-8 py-6 gap-3" 
                  asChild
                >
                  <a href="tel:0695220018">
                    <Phone className="h-6 w-6" />
                    06 95 22 00 18
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
      <CallButton />
    </div>
  );
};

export default Contact;
